import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { WhatsappLogo, UploadSimple, FileZip, FileText, CheckCircle, Warning, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog 3 étapes :
 * 1) Upload .txt ou .zip
 * 2) Dry-run : prévisualisation des stats + mapping des auteurs
 * 3) Import réel + bilan
 */
export default function ImportWhatsAppDialog({ open, onOpenChange, chantierId, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [authorMapping, setAuthorMapping] = useState({});
  const [defaultPrefix, setDefaultPrefix] = useState('(via WhatsApp)');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // reset on close
      setTimeout(() => {
        setStep(1); setFile(null); setPreview(null); setAuthorMapping({});
        setDefaultPrefix('(via WhatsApp)'); setResult(null);
      }, 250);
    }
  }, [open]);

  const onFileSelect = (f) => {
    if (!f) return;
    const name = (f.name || '').toLowerCase();
    if (!name.endsWith('.txt') && !name.endsWith('.zip')) {
      toast.error('Seuls les fichiers .txt ou .zip WhatsApp sont acceptés');
      return;
    }
    setFile(f);
  };

  const runDryRun = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('chantier_id', chantierId);
      fd.append('channel', 'terrain');
      fd.append('dry_run', 'true');
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/jarvis/import-whatsapp`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
      // Pré-remplir avec les suggestions intelligentes du backend
      const suggestions = data.suggested_mapping || {};
      const initial = {};
      (data.authors || []).forEach(a => {
        initial[a] = suggestions[a]?.suggested || '';
      });
      setAuthorMapping(initial);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de lire l'export");
    } finally {
      setLoading(false);
    }
  };

  const runRealImport = async () => {
    setLoading(true);
    try {
      // Ne garder que les mappings remplis
      const mapping = {};
      Object.entries(authorMapping).forEach(([k, v]) => {
        if (v && v.trim()) mapping[k] = v.trim();
      });
      const fd = new FormData();
      fd.append('chantier_id', chantierId);
      fd.append('channel', 'terrain');
      fd.append('dry_run', 'false');
      fd.append('default_author_prefix', defaultPrefix || '');
      fd.append('author_mapping', JSON.stringify(mapping));
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/jarvis/import-whatsapp`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      setStep(3);
      toast.success(`${data.created} message(s) importé(s)`);
      if (onImported) onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  const counts = preview?.counts || {};
  const totalParsed = (counts.text || 0) + (counts.photo || 0) + (counts.vocal || 0) + (counts.video || 0) + (counts.document || 0) + (counts.omitted || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="import-whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2C2C2C]">
            <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
            Importer une conversation WhatsApp
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B6B]">
            Depuis WhatsApp → Infos du groupe → Exporter la discussion (avec médias). Glissez le fichier .zip ou .txt ici.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-2 mb-4 text-xs">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= n ? 'bg-[#C4773B] text-white' : 'bg-[#E0E0DB] text-[#9A9A9A]'}`}>
                {n}
              </div>
              <span className={`${step >= n ? 'text-[#2C2C2C] font-medium' : 'text-[#9A9A9A]'}`}>
                {n === 1 ? 'Fichier' : n === 2 ? 'Aperçu' : 'Bilan'}
              </span>
              {n < 3 && <div className={`flex-1 h-[1px] ${step > n ? 'bg-[#C4773B]' : 'bg-[#E0E0DB]'}`} />}
            </div>
          ))}
        </div>

        {/* ============== ÉTAPE 1 : UPLOAD ============== */}
        {step === 1 && (
          <div className="space-y-4" data-testid="wa-step-1">
            <div
              className="border-2 border-dashed border-[#C4773B]/40 bg-[#FDF6EC]/30 p-8 text-center cursor-pointer hover:bg-[#FDF6EC]/60 transition"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); onFileSelect(e.dataTransfer.files?.[0]); }}
              data-testid="wa-dropzone"
            >
              <UploadSimple size={36} className="mx-auto text-[#C4773B] mb-2" />
              <p className="text-sm font-medium text-[#2C2C2C]">Cliquer ou glisser-déposer</p>
              <p className="text-xs text-[#6B6B6B] mt-1">Format .zip (avec médias) ou .txt — max 50 Mo</p>
              <input
                ref={inputRef}
                type="file"
                accept=".txt,.zip,application/zip,text/plain"
                className="hidden"
                onChange={(e) => onFileSelect(e.target.files?.[0])}
                data-testid="wa-file-input"
              />
            </div>

            {file && (
              <div className="border border-[#E0E0DB] bg-white p-3 flex items-center gap-3" data-testid="wa-selected-file">
                {file.name.toLowerCase().endsWith('.zip') ? <FileZip size={22} className="text-[#C4773B]" /> : <FileText size={22} className="text-[#1E3A5F]" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2C2C2C] truncate">{file.name}</p>
                  <p className="text-xs text-[#9A9A9A]">{(file.size / 1024).toFixed(1)} Ko</p>
                </div>
                <button onClick={() => setFile(null)} className="text-[#9A9A9A] hover:text-[#C75B39]" data-testid="wa-clear-file">
                  <X size={18} />
                </button>
              </div>
            )}

            <div className="text-xs text-[#6B6B6B] bg-[#F5F5F3] border-l-4 border-[#1E3A5F] px-3 py-2">
              <strong>Formats supportés :</strong> iOS <code>[DD/MM/AAAA, HH:MM:SS] Auteur: Message</code> et Android <code>DD/MM/AAAA HH:MM - Auteur: Message</code>. Les photos, vocaux et vidéos du zip sont importés avec leur date d'origine.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="wa-cancel-step1">Annuler</Button>
              <Button onClick={runDryRun} disabled={!file || loading} className="rounded-none bg-[#C4773B] hover:bg-[#B36A31]" data-testid="wa-analyze-btn">
                {loading ? 'Analyse…' : 'Analyser'}
              </Button>
            </div>
          </div>
        )}

        {/* ============== ÉTAPE 2 : APERÇU + MAPPING ============== */}
        {step === 2 && preview && (
          <div className="space-y-4" data-testid="wa-step-2">
            {/* Stats cards */}
            <div className="grid grid-cols-3 gap-2">
              <StatCard label="Messages" value={totalParsed} testid="wa-stat-total" />
              <StatCard label="Auteurs" value={(preview.authors || []).length} testid="wa-stat-authors" />
              <StatCard label="Médias" value={(counts.photo || 0) + (counts.vocal || 0) + (counts.video || 0) + (counts.document || 0)} testid="wa-stat-media" />
            </div>

            <div className="grid grid-cols-4 gap-2 text-xs">
              <PillCount label="Textes" value={counts.text || 0} color="#1E3A5F" />
              <PillCount label="Photos" value={counts.photo || 0} color="#C4773B" />
              <PillCount label="Vocaux" value={counts.vocal || 0} color="#7B9E89" />
              <PillCount label="Omis" value={counts.omitted || 0} color="#9A9A9A" />
            </div>

            {/* Author mapping */}
            {Object.keys(authorMapping).length > 0 && (
              <div>
                <Label className="text-xs font-semibold text-[#2C2C2C] uppercase tracking-wide">Auteurs détectés</Label>
                <p className="text-[11px] text-[#6B6B6B] mb-2">
                  Les noms reconnus dans le chantier sont <strong className="text-[#7B9E89]">pré-remplis automatiquement</strong>. Laissez vide pour appliquer le suffixe par défaut.
                </p>
                <div className="space-y-2 max-h-56 overflow-y-auto border border-[#E0E0DB] p-3 bg-[#FAFAF8]">
                  {Object.keys(authorMapping).map((a) => {
                    const suggestion = preview.suggested_mapping?.[a];
                    return (
                      <div key={a} className="flex items-center gap-2" data-testid={`wa-author-${a}`}>
                        <div className="w-1/3 flex items-center gap-1 min-w-0">
                          <span className="text-xs font-medium text-[#2C2C2C] truncate" title={a}>{a}</span>
                          {suggestion && (
                            <span
                              className="text-[9px] uppercase tracking-wide bg-[#E8F3EA] text-[#4B7A5A] px-1 py-0.5 flex-shrink-0"
                              title={`Correspondance ${Math.round(suggestion.confidence * 100)}% · ${suggestion.source}`}
                              data-testid={`wa-suggestion-${a}`}
                            >
                              ✓ {suggestion.source}
                            </span>
                          )}
                        </div>
                        <span className="text-[#9A9A9A]">→</span>
                        <Input
                          value={authorMapping[a]}
                          onChange={(e) => setAuthorMapping({ ...authorMapping, [a]: e.target.value })}
                          placeholder={`${a} ${defaultPrefix}`}
                          className={`rounded-none text-xs h-8 ${suggestion ? 'border-[#7B9E89] bg-[#F7FBF8]' : ''}`}
                        />
                      </div>
                    );
                  })}
                </div>
                {Object.keys(preview.suggested_mapping || {}).length > 0 && (
                  <p className="text-[10px] text-[#7B9E89] mt-1" data-testid="wa-smart-match-info">
                    {Object.keys(preview.suggested_mapping).length} auteur(s) reconnu(s) parmi les intervenants, propriétaires et entreprises du chantier.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-[#2C2C2C] uppercase tracking-wide">Suffixe pour auteurs non mappés</Label>
              <Input
                value={defaultPrefix}
                onChange={(e) => setDefaultPrefix(e.target.value)}
                placeholder="(via WhatsApp)"
                className="rounded-none text-xs mt-1"
                data-testid="wa-default-prefix"
              />
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-none" data-testid="wa-back-step2">Retour</Button>
              <Button onClick={runRealImport} disabled={loading} className="rounded-none bg-[#C4773B] hover:bg-[#B36A31]" data-testid="wa-confirm-import">
                {loading ? 'Import…' : `Importer ${totalParsed} message(s)`}
              </Button>
            </div>
          </div>
        )}

        {/* ============== ÉTAPE 3 : BILAN ============== */}
        {step === 3 && result && (
          <div className="space-y-4 text-center" data-testid="wa-step-3">
            <div className="inline-flex p-3 bg-[#E8F3EA] rounded-full">
              <CheckCircle size={42} className="text-[#7B9E89]" weight="fill" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#2C2C2C]">Import réussi</h3>
              <p className="text-sm text-[#6B6B6B] mt-1">
                <strong>{result.created}</strong> message(s) ajouté(s) au Journal terrain
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-left">
              <StatCard label="Textes" value={result.counts?.text || 0} testid="wa-result-text" />
              <StatCard label="Photos" value={result.counts?.photo || 0} testid="wa-result-photo" />
              <StatCard label="Vocaux" value={result.counts?.vocal || 0} testid="wa-result-vocal" />
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="text-left bg-[#FDF6EC] border-l-4 border-[#C4773B] p-2 text-xs">
                <Warning size={14} className="inline mr-1 text-[#C4773B]" />
                {result.errors.length} ligne(s) en erreur — les autres ont bien été importées.
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button onClick={() => onOpenChange(false)} className="rounded-none bg-[#1E3A5F] hover:bg-[#172C47] text-white" data-testid="wa-close-result">Fermer</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-[#E0E0DB] bg-white p-3 text-center" data-testid={testid}>
      <p className="text-2xl font-bold text-[#2C2C2C]">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-[#9A9A9A]">{label}</p>
    </div>
  );
}

function PillCount({ label, value, color }) {
  return (
    <div className="flex items-center justify-between border border-[#E0E0DB] px-2 py-1 bg-white">
      <span className="text-[10px] text-[#6B6B6B]">{label}</span>
      <span className="font-bold text-xs" style={{ color }}>{value}</span>
    </div>
  );
}
